import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getStatusColor, CONTRACT_STATUSES, ENTITIES, buildEmailHTML, buildFolderPath, DOC_TYPES, normalizeDocType } from "../../services/contractService";
import { generateContractDraft, generateCrafDraft, listTemplateFiles } from "../../services/templateService";
import { ObligationsList } from "./ObligationsList";
import { ApproverSelector } from "../Execution/ApproverSelector";
import { executionService } from "../../services/executionService";
import { crafExecutionService } from "../../services/crafExecutionService";

// Map code → emoji alias used in existing display logic
const DOC_TYPES_WITH_EMOJI = DOC_TYPES.map(t => ({ ...t, emoji: t.code }));
const getDocType = (id) => DOC_TYPES_WITH_EMOJI.find(d=>d.id===id)||DOC_TYPES_WITH_EMOJI[DOC_TYPES_WITH_EMOJI.length-1];
const getFileIcon = (n) => ({pdf:"[PDF]",docx:"[DOC]",doc:"[DOC]",xlsx:"[XLS]",xls:"[XLS]",pptx:"[PPT]",ppt:"[PPT]",png:"[IMG]",jpg:"[IMG]",jpeg:"[IMG]"})[(n||"").split(".").pop().toLowerCase()]||"[FILE]";
const fmtSize = (b) => !b?"":b<1024?b+"B":b<1048576?Math.round(b/1024)+"KB":(b/1048576).toFixed(1)+"MB";

// Infer doc type from filename for drive-fetched items, then normalize to categories
const guessDocType = (name) => {
  const n = (name||"").toLowerCase();

  // Check for type code prefix (e.g., "[CONTRACT]_", "[SOW]_", "[COMM]_", "[EXECUTED]_", "Executed-")
  if (n.startsWith("[contract]_")) return "contract_draft";
  if (n.startsWith("[sow]_")) return "scope_of_work";
  if (n.startsWith("[comm]_")) return "commercials";
  if (n.startsWith("[executed]_") || n.startsWith("executed-")) return "executed_contract";

  let oldType = "other";
  if (n.includes("nda") || n.includes("non-disclosure")) oldType = "executed_contract";
  else if (n.includes("addend") || n.includes("amendment")) oldType = "addendum";
  else if (n.includes("craf") || n.includes("approval")) oldType = "approval";
  else if (n.includes("award")) oldType = "award_letter";
  else if (n.includes("receipt") || n.includes("invoice")) oldType = "receipt";
  else if (n.includes("completion") || n.includes("certificate") || n.includes("cert")) oldType = "completion_cert";
  else if (n.includes("insurance") || n.includes("bond")) oldType = "insurance";
  else if (n.includes("scope") || n.includes("sow")) oldType = "scope_of_work";
  else if (n.includes("kpi") || n.includes("performance")) oldType = "kpi";
  else if (n.includes("pricing") || n.includes("rate")) oldType = "pricing";
  else if (n.includes("draft")) oldType = "contract_draft";
  else {
    const ext = n.split(".").pop();
    if (["eml","msg"].includes(ext)) oldType = "correspondence";
    else if (/[a-z]+-\d/.test(n) && ["docx","doc"].includes(ext)) oldType = "contract_draft";
    else if (/[a-z]+-\d/.test(n) && ext === "pdf") oldType = "original_document";
  }
  return normalizeDocType(oldType);
};
const InfoRow = ({label,value}) => (
  <div className="flex gap-6 border-b border-light-border dark:border-dark-border py-2.5 px-4">
    <div className="w-40 text-base text-light-text-secondary dark:text-dark-text-secondary flex-shrink-0">{label}</div>
    <div className="text-base text-light-text dark:text-dark-text flex-1">{value||"--"}</div>
  </div>
);

const AddendaSection = ({ contractId, contractRef, service, navigate }) => {
  const [addenda, setAddenda] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contractId) return;
    service.getListItems("Contracts")
      .then(d => {
        const all = (d?.value || [])
          .map(i => ({...i.fields, id: i.id}))
          .filter(c => (c.ContractType && c.ContractType.includes("Addendum")) && (c.ParentContractId === contractId || c.ParentContractRef === contractRef))
          .sort((a,b) => new Date(a.RequestDate||0) - new Date(b.RequestDate||0));
        setAddenda(all);
      })
      .catch(() => setAddenda([]))
      .finally(() => setLoading(false));
  }, [contractId, contractRef]);

  if (!loading && addenda.length === 0) return null;

  return (
    <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl overflow-hidden">
      <div className="px-5 py-3.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border-b border-light-border dark:border-dark-border flex items-center gap-2.5">
        <div className="w-0.75 h-5 bg-purple-600 rounded-sm"/>
        <div>
          <div className="font-serif text-lg font-medium text-light-text dark:text-dark-text leading-none">Addenda</div>
          <div className="text-base text-light-text-secondary dark:text-dark-text-secondary mt-0.5">{addenda.length} addendum{addenda.length !== 1 ? "s" : ""} attached to this contract</div>
        </div>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-center py-6 text-light-text-secondary dark:text-dark-text-secondary">Loading addenda...</div>
        ) : (
          <div className="flex flex-col gap-0">
            {addenda.map((a, i) => (
              <div key={a.id}
                onClick={() => navigate("/contracts/" + a.id)}
                className={`flex items-start gap-3.5 py-3.5 cursor-pointer hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors ${i < addenda.length-1 ? "border-b border-light-border dark:border-dark-border" : ""}`}
              >
                {/* Timeline indicator */}
                <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                  <div className="w-5.5 h-5.5 rounded-full bg-purple-600 flex items-center justify-center text-sm font-bold text-white font-mono">{i+1}</div>
                  {i < addenda.length-1 && <div className="w-0.5 flex-1 bg-light-border dark:bg-dark-border mt-1 min-h-5"/>}
                </div>
                <div className="flex-1 overflow-hidden">
                  <div className="text-base font-semibold text-light-text dark:text-dark-text mb-0.75 overflow-hidden text-ellipsis whitespace-nowrap">{a.Title||"Untitled Addendum"}</div>
                  <div className="flex gap-3 text-base text-light-text-secondary dark:text-dark-text-secondary font-mono flex-wrap">
                    <span className="text-accent">{a.RefNo}</span>
                    {a.RequestDate && <span>Added {new Date(a.RequestDate).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>}
                    {a.VendorName && <span>{a.VendorName}</span>}
                  </div>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-base font-medium" style={{backgroundColor: getStatusColor(a.Status)+"20", color: getStatusColor(a.Status)}}>{a.Status||"--"}</span>
                  <span className="text-base text-light-text-secondary dark:text-dark-text-secondary">→</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
const DocTypeTag = ({typeId}) => {
  const t=getDocType(typeId);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-medium whitespace-nowrap"
      style={{backgroundColor: t.color+"22", color: t.color}}>
      {t.emoji} {t.label}
    </span>
  );
};

const UploadModal = ({ onClose, onUpload, uploading }) => {
  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState("contract_draft");
  const [desc, setDesc] = useState("");
  const [drag, setDrag] = useState(false);
  const isExecutedContract = docType === "executed_contract";
  const allowedFileTypes = isExecutedContract ? ".pdf" : ".pdf,.docx,.doc,.xlsx,.xls,.pptx,.png,.jpg,.jpeg,.zip";
  const fileTypeText = isExecutedContract ? "PDF only" : "PDF, Word, Excel, PowerPoint, Images, ZIP";

  const addFiles = (nf) => {
    const newFiles = Array.from(nf).filter(f => {
      if (isExecutedContract && !f.name.toLowerCase().endsWith('.pdf')) {
        alert("Executed contracts must be PDF files only");
        return false;
      }
      return !files.find(x => x.name === f.name);
    });
    setFiles(p => [...p, ...newFiles]);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[400]">
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-8 w-full max-w-md max-h-[90vh] relative shadow-2xl overflow-auto">
        <div className="absolute top-0 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-transparent via-yellow-600 to-transparent"/>

        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-xl font-medium text-light-text dark:text-dark-text m-0">Add Document</h3>
          <button onClick={onClose} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-2xl">×</button>
        </div>

        <div className="mb-3.5">
          <label className="block text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1.5 font-medium">Document Type <span className="text-yellow-600">*</span></label>
          <select value={docType} onChange={e=>setDocType(e.target.value)} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600">
            {DOC_TYPES_WITH_EMOJI.map(t=><option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
          </select>
          <div style={{height:"3px", borderRadius:"2px", background: getDocType(docType).color, marginTop:"5px", width:"35%", transition:"background 0.2s"}}/>
          {isExecutedContract && <div className="text-xs text-orange-500 mt-2 font-medium">⚠️ PDF files only for Executed Contracts</div>}
        </div>

        <div className="mb-3.5">
          <label className="block text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1.5 font-medium">Description / Notes (optional)</label>
          <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="e.g. Signed document received 12 May 2025" className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm focus:outline-none focus:ring-2 focus:ring-yellow-600"/>
        </div>

        <div
          onDragOver={e=>{e.preventDefault();setDrag(true);}}
          onDragLeave={()=>setDrag(false)}
          onDrop={e=>{e.preventDefault();setDrag(false);addFiles(e.dataTransfer.files);}}
          onClick={()=>document.getElementById("lx-doc-input").click()}
          className={`border-2 border-dashed rounded-xl p-5.5 text-center mb-3 cursor-pointer transition-all ${
            drag ? "border-yellow-600 bg-yellow-600/10" : "border-light-border dark:border-dark-border bg-transparent"
          }`}
        >
          <input id="lx-doc-input" type="file" multiple className="hidden" accept={allowedFileTypes} onChange={e=>addFiles(e.target.files)}/>
          <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">Drag and drop or <span className="text-yellow-600">click to browse</span></div>
          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{fileTypeText}</div>
        </div>

        {files.length>0&&(
          <div className="max-h-32 overflow-y-auto flex flex-col gap-1 mb-3.5">
            {files.map((f,i)=>(
              <div key={i} className="flex items-center gap-2 p-1.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded text-sm">
                <span>{getFileIcon(f.name)}</span>
                <span className="flex-1 text-light-text-secondary dark:text-dark-text-secondary overflow-hidden text-ellipsis whitespace-nowrap">{f.name}</span>
                <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary font-mono">{fmtSize(f.size)}</span>
                <button onClick={()=>setFiles(p=>p.filter((_,j)=>j!==i))} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm">×</button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2.5 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">Cancel</button>
          <button onClick={()=>onUpload(files,docType,desc)} disabled={!files.length||uploading}
            className={`px-7 py-2.5 border-none rounded-lg font-semibold text-sm transition-colors ${
              files.length ? "bg-yellow-600 hover:bg-yellow-700 text-slate-900 cursor-pointer" : "bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed"
            }`}>
            {uploading?"Uploading...":"Upload"+(files.length?" ("+files.length+")":"")}
          </button>
        </div>
      </div>
    </div>
  );
};

const DocCard = ({ doc, onDelete, isAdmin, service, onDocTypeChange, driveId, isCategoryManager }) => {
  const canManage = isAdmin || isCategoryManager;
  const [hover, setHover] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [movingType, setMovingType] = useState(false);
  const t = getDocType(doc.DocType);
  const isWordDoc = doc.FileName.toLowerCase().endsWith('.docx') || doc.FileName.toLowerCase().endsWith('.doc');
  const isDraftOnly = doc.DocType === "contract_draft";

  const loadVersions = async () => {
    if (versions.length > 0) { setShowVersions(!showVersions); return; }
    setLoadingVersions(true);
    try {
      const vers = await service.getFileVersions(driveId, doc.id);
      console.log("📋 File versions:", vers);
      const versionList = (vers?.value || []).map(v => ({
        id: v.id,
        lastModifiedBy: v.lastModifiedBy?.user?.displayName || "Unknown User",
        lastModifiedDateTime: v.lastModifiedDateTime,
        isCurrentVersion: v.isCurrentVersion || false,
      })).reverse();
      console.log("📋 Mapped versions:", versionList);
      setVersions(versionList);
      setShowVersions(true);
    } catch (e) {
      console.error("Error loading versions:", e);
      setVersions([{
        lastModifiedBy: "Error: Could not load version history",
        lastModifiedDateTime: new Date().toISOString(),
      }]);
      setShowVersions(true);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleMoveType = async (newType) => {
    if (newType === doc.DocType || !isAdmin) return;
    setMovingType(true);
    try {
      // Update document metadata in FileID list instead of renaming file
      if (doc.metadataId) {
        await service.updateListItem("FileID", doc.metadataId, {
          DocumentType: newType,
          LastCategoryChange: new Date().toISOString(),
        });
      } else {
        // Create new metadata record if it doesn't exist
        await service.createListItem("FileID", {
          FileID: doc.id,
          FilePath: doc.FileName,
          DocumentType: newType,
          CreatedDate: new Date().toISOString(),
        });
      }
      onDocTypeChange();
      setShowTypeMenu(false);
    } catch (e) {
      console.error("Error moving document:", e);
      if (e.message?.includes("specified list was not found")) {
        alert("📋 FileID list not found.\n\n✅ Creating it now... Please try again in a moment.\n\nIf it still fails, create a 'FileID' list in SharePoint with columns: FileID, FilePath, DocumentType, CreatedDate, LastCategoryChange");
      } else {
        alert("Failed to move document: " + e.message);
      }
    } finally {
      setMovingType(false);
    }
  };

  return (
    <div onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      className={`border rounded-lg p-3 flex items-center gap-3 transition-all relative overflow-visible ${
        hover ? "bg-light-bg-tertiary dark:bg-dark-bg-tertiary border-light-border/50 dark:border-dark-border/50" : "bg-transparent border-light-border dark:border-dark-border"
      }`}>
      <div className="text-lg shrink-0 font-mono">{getFileIcon(doc.FileName)}</div>
      <div className="flex-1 overflow-visible min-w-0">
        <div className="flex items-center gap-3 mb-0.75 flex-wrap relative">
          <span className="text-sm font-medium text-light-text dark:text-dark-text overflow-hidden text-ellipsis whitespace-nowrap">{doc.FileName}</span>
          {canManage && (
            <div className="relative z-10">
              <button onClick={() => setShowTypeMenu(!showTypeMenu)} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold whitespace-nowrap shrink-0 cursor-pointer transition-all" style={{backgroundColor: t.color+"22", color: t.color, border: "1px solid "+t.color+"44"}} title="Move to another category">
                {t.code} ▼
              </button>
              {showTypeMenu && (
                <div className="absolute top-[calc(100%+4px)] left-0 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded text-sm min-w-[150px] z-[1050] shadow-lg">
                  {DOC_TYPES.map(type => (
                    <button key={type.id} onClick={() => handleMoveType(type.id)} disabled={movingType || type.id === doc.DocType}
                      className={`w-full px-3 py-2 bg-transparent border-b border-light-border dark:border-dark-border text-xs text-left transition-colors ${
                        type.id === doc.DocType ? "text-light-text-secondary dark:text-dark-text-secondary opacity-50 cursor-default" : "text-light-text dark:text-dark-text cursor-pointer hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary"
                      }`}>
                      {type.code === doc.DocType.split('_')[0] || type.id === doc.DocType ? "✓ " : ""}{type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!isAdmin && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold whitespace-nowrap shrink-0" style={{backgroundColor: t.color+"22", color: t.color}}>{t.code}</span>}
        </div>
        {doc.Description&&<div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-0.75 leading-relaxed">{doc.Description}</div>}
        <div className="flex gap-4 text-xs text-light-text-secondary dark:text-dark-text-secondary font-sans flex-wrap">
          {doc.FileSize?<span>{fmtSize(parseInt(doc.FileSize))}</span>:null}
          {doc.UploadedBy?<span>by {doc.UploadedBy.split("@")[0]}</span>:null}
          {doc.UploadedDate?<span>{new Date(doc.UploadedDate).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>:null}
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0 items-center relative z-5">
        <button onClick={loadVersions} className="px-2 py-1.5 bg-light-border/20 dark:bg-dark-border/20 border border-light-border dark:border-dark-border rounded text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-xs transition-all hover:border-light-border dark:hover:border-dark-border" title="Version history - all edits">
          {loadingVersions ? "..." : "📋"}
        </button>
        <a href={doc.WebUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-900/20 border border-blue-700/30 rounded text-blue-400 text-xs font-medium hover:text-blue-300 hover:border-blue-500/50 transition-colors whitespace-nowrap">
          Open ↗
        </a>
        {canManage && doc.DocType !== "executed_contract" && <button onClick={()=>onDelete(doc)} className="px-2 py-1.5 bg-transparent border border-light-border dark:border-dark-border rounded text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-xs transition-all hover:border-red-600 hover:text-red-500 dark:hover:border-red-600 dark:hover:text-red-400" title="Delete document">✕</button>}
        {canManage && doc.DocType === "executed_contract" && <button disabled className="px-2 py-1.5 bg-transparent border border-light-border/40 dark:border-dark-border/40 rounded text-light-text-secondary/40 dark:text-dark-text-secondary/40 cursor-not-allowed text-xs opacity-50" title="Executed contracts cannot be deleted">🔒</button>}
      </div>

      {/* Version History Dropdown - All Edits */}
      {showVersions && (
        <div className="absolute top-[calc(100%+4px)] right-0 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded text-xs min-w-[340px] max-h-[350px] overflow-y-auto z-[1000] shadow-xl">
          <div className="px-4 py-3 border-b border-light-border dark:border-dark-border text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary sticky top-0 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">✏️ Edit History - All Changes</div>
          {versions.length === 0 ? (
            <div className="px-4 py-3 text-xs text-light-text-secondary dark:text-dark-text-secondary">No versions found</div>
          ) : (
            <div>
              {versions.map((v, i) => (
                <div key={i} className="px-4 py-3 border-b border-light-border dark:border-dark-border text-xs">
                  <div className="font-semibold text-light-text dark:text-dark-text mb-1">{v.lastModifiedBy}</div>
                  <div className="text-light-text-secondary dark:text-dark-text-secondary text-xs">
                    {new Date(v.lastModifiedDateTime).toLocaleString("en-GB", {
                      day:"2-digit", month:"short", year:"numeric",
                      hour:"2-digit", minute:"2-digit"
                    })}
                  </div>
                  {v.isCurrentVersion && <div className="text-xs text-yellow-600 dark:text-yellow-500 mt-1 font-semibold">📌 Current Version</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DocumentsSection = ({ contractId, contractRef, folderPath, driveId, isAdmin, service, addNotification, currentUser, isCategoryManager }) => {
  const canUpload = isAdmin || isCategoryManager;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUp, setShowUp] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("contract_draft");

  const loadDocs = async () => {
    if (!driveId||!folderPath) { setLoading(false); return; }
    setLoading(true);
    try {
      // Fetch files from folder
      const d = await service.getFolderContents(driveId, folderPath);
      const files = (d?.value||[]).filter(i=>!i.folder);

      // Fetch document metadata
      let metadata = [];
      try {
        const metaData = await service.getListItems("FileID");
        metadata = (metaData?.value||[]).map(m=>({
          id: m.id,
          FileID: m.fields?.FileID,
          DocumentType: m.fields?.DocumentType,
          ...m.fields
        }));
      } catch (e) {
        console.warn("FileID list not found, using filename detection");
      }

      // Map files with metadata
      const items = files.map(i => {
        const meta = metadata.find(m => m.FileID === i.id);
        return {
          id: i.id,
          metadataId: meta?.id,
          FileName: i.name,
          WebUrl: i.webUrl,
          FileSize: String(i.size||0),
          DocType: meta?.DocumentType || guessDocType(i.name),
          UploadedDate: i.lastModifiedDateTime||i.createdDateTime,
          UploadedBy: i.createdBy?.user?.displayName||i.lastModifiedBy?.user?.displayName||"",
        };
      });
      setDocs(items);
    } catch(e) {
      console.error("Error loading documents:", e);
      setDocs([]);
    }
    finally { setLoading(false); }
  };

  useEffect(()=>{ if(contractId) loadDocs(); },[contractId, driveId, folderPath]);

  const handleUpload = async (files,docType,description) => {
    if (!files.length||!driveId) { addNotification("Drive not configured","error"); return; }
    setUploading(true);
    try {
      for (const file of files) {
        const buf = await file.arrayBuffer();
        const uploadedFile = await service.uploadFile(driveId, folderPath, file.name, buf);

        // Create metadata record for the uploaded file
        if (uploadedFile?.id) {
          try {
            await service.createListItem("FileID", {
              FileID: uploadedFile.id,
              FilePath: file.name,
              DocumentType: docType,
              CreatedDate: new Date().toISOString(),
            });
          } catch (e) {
            console.warn("Failed to create metadata for file:", e);
          }
        }
      }
      await loadDocs();
      addNotification(files.length+" document"+(files.length>1?"s":"")+" added","success");
      setShowUp(false);
    } catch(e){ addNotification("Upload error: "+e.message,"error"); }
    setUploading(false);
  };

  const handleDelete = async (doc) => {
    if (!window.confirm("Delete this file from SharePoint? This cannot be undone.")) return;
    try {
      await service.deleteFile(driveId, doc.id);
      setDocs(p=>p.filter(d=>d.id!==doc.id));
      addNotification("File deleted","success");
    } catch(e){ addNotification("Delete error: "+e.message,"error"); }
  };

  return (
    <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl overflow-visible">
      <div className="px-5 py-3.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border-b border-light-border dark:border-dark-border flex items-center justify-between flex-wrap gap-2.5">
        <div>
          <div className="font-serif text-lg font-medium text-light-text dark:text-dark-text leading-none">Contract Documents</div>
          <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-0.75">{docs.length} file{docs.length!==1?"s":""} attached</div>
        </div>
        {canUpload&&<button onClick={()=>setShowUp(true)} className="flex items-center gap-1.5 px-3.5 py-1.75 bg-yellow-600 border-none rounded-lg text-slate-900 cursor-pointer text-sm font-semibold hover:bg-yellow-700 transition-colors">+ Add Document</button>}
      </div>

      {/* Document Tabs */}
      <div className="border-b border-light-border dark:border-dark-border flex bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
        {[
          {id:"contract_draft",label:"Contract Draft",icon:"📄"},
          {id:"scope_of_work",label:"Scope of Work",icon:"📋"},
          {id:"commercials",label:"Commercials",icon:"💰"},
          {id:"executed_contract",label:"Executed Contract",icon:"✅"}
        ].map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)} className={`flex-1 px-5 py-3.5 bg-transparent border-none cursor-pointer text-sm font-medium flex items-center justify-center gap-1.5 transition-all whitespace-nowrap ${
            activeTab===tab.id ? "border-b-3 border-yellow-600 text-yellow-600" : "text-light-text-secondary dark:text-dark-text-secondary border-b-3 border-transparent"
          }`}>
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="p-5">
        {loading?(
          <div className="text-center py-10 text-light-text-secondary dark:text-dark-text-secondary">
            <div className="w-8 h-8 border-3 border-light-border dark:border-dark-border border-t-yellow-600 rounded-full animate-spin mx-auto mb-3"/>
            Loading documents...
          </div>
        ):(() => {
          const filtered = docs.filter(d => d.DocType === activeTab);
          return filtered.length === 0 ? (
            <div className="text-center py-9 px-4">
              <div className="text-5xl mb-3 opacity-40">📋</div>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
                {`No ${activeTab.replace(/_/g, " ")} documents.`}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {filtered.map((doc,i)=><DocCard key={doc.id||i} doc={doc} onDelete={handleDelete} isAdmin={isAdmin} service={service} onDocTypeChange={loadDocs} driveId={driveId} isCategoryManager={isCategoryManager}/>)}
            </div>
          );
        })()}
      </div>
      {showUp&&<UploadModal onClose={()=>setShowUp(false)} onUpload={handleUpload} uploading={uploading}/>}
    </div>
  );
};

// Lets the admin pick which uploaded template to generate the CRAF document from
// (unlike the main contract draft, this is not auto-mapped by contract type).
const CrafTemplateSelectModal = ({ service, onGenerate, onCancel, generating }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    listTemplateFiles(service)
      .then(files => {
        setTemplates(files);
        const crafDefault = files.find(f => f.name.toLowerCase().includes("craf"));
        if (crafDefault) setSelected(crafDefault.name);
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [service]);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5">
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-8 w-full max-w-lg">
        <h2 className="font-serif text-2xl font-medium text-light-text dark:text-dark-text mb-2">Generate CRAF Document</h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6 text-sm">Select the template to generate the Contract Review and Approval Form from. Contract details will be auto-filled into the placeholders. You'll be able to open and edit the resulting Word document before sending it for execution.</p>

        {loading ? (
          <div className="text-center py-8 text-light-text-secondary dark:text-dark-text-secondary">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-8 text-light-text-secondary dark:text-dark-text-secondary">No templates found. Upload one in the Template Library first.</div>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-3">Template:</label>
            <select value={selected} onChange={e=>setSelected(e.target.value)} className="w-full px-4 py-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-2 focus:ring-yellow-600">
              <option value="">Select a template...</option>
              {templates.map(t => <option key={t.name} value={t.name}>{t.name.replace(/_/g," ").replace(/\.docx$/i,"")}</option>)}
            </select>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} disabled={generating} className="px-6 py-2.5 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={()=>onGenerate(selected)} disabled={generating || !selected} className={`px-6 py-2.5 rounded-lg font-semibold text-sm text-white transition-all ${generating || !selected ? "bg-indigo-600/50 cursor-not-allowed opacity-50" : "bg-indigo-600 hover:bg-indigo-700 cursor-pointer"}`}>
            {generating ? "Generating..." : "Generate CRAF"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ContractDetail = () => {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { theme } = useTheme();
  const { service, addNotification, isAdmin, currentUser, contractTypes } = useApp();
  const [contract, setContract]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [showShare, setShowShare]   = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [shareMsg, setShareMsg]     = useState("");
  const [shareMode, setShareMode]   = useState("regular");
  const [shareSearchQuery, setShareSearchQuery] = useState("");
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [shareSearching, setShareSearching] = useState(false);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [newStatus, setNewStatus]   = useState("");
  const [driveId, setDriveId]       = useState(null);
  const [folderPath, setFolderPath] = useState("");
  const [showEdit, setShowEdit]     = useState(false);
  const [editForm, setEditForm]     = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [showRegen, setShowRegen]   = useState(false);
  const [regenRunning, setRegenRunning] = useState(false);
  const [regenLog, setRegenLog]     = useState([]);
  const [docsKey, setDocsKey]       = useState(0);
  const [updatingReady, setUpdatingReady] = useState(false);
  const [showExecutionModal, setShowExecutionModal] = useState(false);
  const [executionInProgress, setExecutionInProgress] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [loadingReviewers, setLoadingReviewers] = useState(false);

  // CRAF (Contract Review and Approval Form) state
  const [showCrafTemplateModal, setShowCrafTemplateModal] = useState(false);
  const [crafGenerating, setCrafGenerating] = useState(false);
  const [crafDraftExists, setCrafDraftExists] = useState(false);
  const [showCrafExecutionModal, setShowCrafExecutionModal] = useState(false);
  const [crafExecutionInProgress, setCrafExecutionInProgress] = useState(false);
  const [crafExecution, setCrafExecution] = useState(null);
  const [crafStatusLoading, setCrafStatusLoading] = useState(false);

  // Check if current user is the category manager for this contract
  const isCategoryManager = contract && currentUser?.displayName &&
    (contract.CategoryManager || "").toLowerCase() === (currentUser.displayName || "").toLowerCase();
  const canEdit = isAdmin || isCategoryManager;

  useEffect(()=>{
    if (!id) return;
    service.getListItem("Contracts",id).then(async d=>{
      const f={...d.fields,id:d.id}; setContract(f); setNewStatus(f.Status||"");

      if(f.ContractType && f.ContractType.includes("Addendum") && f.ParentContractId) {
        // Use the parent contract's folder — that's where the addendum docs live
        const parentData = await service.getListItem("Contracts", f.ParentContractId).catch(()=>null);
        const pf = parentData?.fields;
        if(pf) {
          setFolderPath(buildFolderPath(pf.Entity,pf.Department,pf.Year||new Date().getFullYear(),pf.Month||1,pf.RefNo||""));
        } else {
          // Fallback: parent not found, use own fields
          setFolderPath(buildFolderPath(f.Entity,f.Department,f.Year||new Date().getFullYear(),f.Month||1,f.RefNo||""));
        }
      } else {
        setFolderPath(buildFolderPath(f.Entity,f.Department,f.Year||new Date().getFullYear(),f.Month||1,f.RefNo||""));
      }

      // Load reviewers if contract is in review
      if (f.Status === "In Review") {
        loadReviewers(id);
      }
    }).catch(()=>addNotification("Error loading contract","error")).finally(()=>setLoading(false));
    service.getDrives().then(d=>{ const dr=d?.value?.find(x=>x.name==="ContractDocuments"); if(dr) setDriveId(dr.id); }).catch(()=>{});
  },[id]);

  // Load reviewers for contracts in review
  const loadReviewers = async (contractId) => {
    try {
      setLoadingReviewers(true);
      const reviewData = await service.getListItems("ReviewStatus");
      const contractReviewers = (reviewData?.value || [])
        .filter(r => r.fields.ContractIDLookupId === contractId || r.fields.ContractIDLookupId?.toString() === contractId?.toString())
        .map(r => ({
          id: r.id,
          name: r.fields.ReviewerName || "",
          email: r.fields.ReviewerEmail || "",
          status: r.fields.Status || "Not Started",
          createdAt: r.fields.CreatedAt || null,
          completedAt: r.fields.CompletedAt || null
        }))
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      setReviewers(contractReviewers);
    } catch (error) {
      console.error("Error loading reviewers:", error);
      setReviewers([]);
    } finally {
      setLoadingReviewers(false);
    }
  };

  // CRAF file naming — kept distinct from the main contract draft/PDF names
  // so the two never collide in the same folder.
  const getCrafDraftFileName = (refNo) => `CRAF-${String(refNo||"").replace(/[/\\]/g,"-")}.docx`;

  const loadCrafStatus = async () => {
    if (!driveId || !folderPath || !contract?.id) return;
    setCrafStatusLoading(true);
    try {
      const folderContents = await service.getFolderContents(driveId, folderPath).catch(() => ({ value: [] }));
      const files = (folderContents?.value || []).filter(i => !i.folder);
      const crafDraftFileName = getCrafDraftFileName(contract.RefNo);
      const hasCrafDraft = files.some(f => f.name.toLowerCase() === crafDraftFileName.toLowerCase());
      setCrafDraftExists(hasCrafDraft);

      const latestCrafExec = await crafExecutionService.getLatestCrafExecutionForContract(service, contract.id);
      setCrafExecution(latestCrafExec);
    } catch (e) {
      console.warn("[ContractDetail] Error loading CRAF status:", e);
    } finally {
      setCrafStatusLoading(false);
    }
  };

  useEffect(() => {
    if (driveId && folderPath && contract?.id) {
      loadCrafStatus();
    }
  }, [driveId, folderPath, contract?.id, docsKey]);

  // User search for sharing
  useEffect(() => {
    if (!shareSearchQuery.trim()) {
      setShareSearchResults([]);
      return;
    }
    const searchUsers = async () => {
      try {
        setShareSearching(true);
        const results = await service.searchUsers(shareSearchQuery);
        const users = (results?.value || []).map(u => ({
          id: u.id,
          displayName: u.displayName,
          mail: u.mail,
          jobTitle: u.jobTitle
        }));
        setShareSearchResults(users);
      } catch (e) {
        console.error("User search error:", e);
        setShareSearchResults([]);
      } finally {
        setShareSearching(false);
      }
    };
    const debounce = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounce);
  }, [shareSearchQuery, service]);

  const addReviewer = (user) => {
    const email = user.mail;
    if (!selectedReviewers.find(r => r.mail === email)) {
      setSelectedReviewers([...selectedReviewers, user]);
    }
    setShareSearchQuery("");
    setShareSearchResults([]);
    setShowSearchDropdown(false);
  };

  const removeReviewer = (email) => {
    setSelectedReviewers(selectedReviewers.filter(r => r.mail !== email));
  };

  const addManualEmail = () => {
    if (shareSearchQuery.trim() && !selectedReviewers.find(r => r.mail === shareSearchQuery.trim())) {
      setSelectedReviewers([...selectedReviewers, {
        mail: shareSearchQuery.trim(),
        displayName: shareSearchQuery.trim().split("@")[0]
      }]);
      setShareSearchQuery("");
      setShareSearchResults([]);
    }
  };

  const handleShare = async () => {
    if (!selectedReviewers.length && !shareEmail) return;
    try {
      let documentUrl;
      let emailType = "documentShared";
      const emails = selectedReviewers.map(r => r.mail).concat(shareEmail.split(",").map(e => e.trim()).filter(e => e));
      const uniqueEmails = [...new Set(emails)];

      if (shareMode === "reviewer") {
        const token = generateReviewerToken(id);
        documentUrl = window.location.origin + "/reviewer/" + id + "/" + token;
        emailType = "reviewerShared";

        // Update contract status to "In Review"
        try {
          console.log('[ContractDetail] Updating contract status to "In Review"');
          await service.updateListItem("Contracts", id, {
            Status: "In Review"
          });
          console.log('[ContractDetail] ✅ Contract status updated to "In Review"');
        } catch (e) {
          console.warn('[ContractDetail] Could not update contract status:', e);
        }

        // Create ReviewStatus records for each reviewer
        for (const email of uniqueEmails) {
          try {
            console.log('[ContractDetail] Creating ReviewStatus with ContractIDLookupId:', id, 'for email:', email);
            const reviewStatusData = await service.createListItem("ReviewStatus", {
              Title: `Review - ${contract.RefNo}`,
              ContractIDLookupId: id,
              ContractRefNo: contract.RefNo,
              ReviewerEmail: email,
              Status: "Not Started",
              CreatedAt: new Date().toISOString(),
              RemindersEnabled: true,
            });
            console.log('[ContractDetail] ✅ Created ReviewStatus for', email, 'ID:', reviewStatusData?.id, 'ContractIDLookupId:', reviewStatusData?.fields?.ContractIDLookupId);
          } catch (e) {
            console.error('[ContractDetail] ❌ Could not create ReviewStatus:', {
              error: e.message,
              errorFull: e,
              contractId: id,
              email: email
            });
          }
        }
      } else {
        documentUrl = window.location.origin + "/contracts/" + id;
      }

      const html = buildEmailHTML(emailType, {
        title: contract.Title,
        refNo: contract.RefNo,
        sharedBy: currentUser?.displayName,
        message: shareMsg,
        documentUrl: documentUrl,
      });

      await service.sendEmail(uniqueEmails, "[LexCore] Contract Shared: "+contract.RefNo, html);
      addNotification("Notification sent","success");
      setShowShare(false);
      setShareEmail("");
      setShareMsg("");
      setShareMode("regular");
      setSelectedReviewers([]);
      setShareSearchQuery("");
    } catch(e){ addNotification("Share failed: "+e.message,"error"); }
  };

  const toggleReady = async () => {
    setUpdatingReady(true);
    try {
      const newValue = contract.IsReady === "Yes" ? "No" : "Yes";
      await service.updateListItem("Contracts", id, { IsReady: newValue });
      setContract(p=>({...p, IsReady: newValue}));
      addNotification(`Contract marked as ${newValue === "Yes" ? "ready" : "incomplete"}`, "success");
    } catch (err) {
      console.error("Failed to update IsReady:", err);
      addNotification("Error updating ready status", "error");
    } finally {
      setUpdatingReady(false);
    }
  };

  const handleInitiateExecution = async (selectedApprovers) => {
    setExecutionInProgress(true);
    try {
      console.log('[ContractDetail] Initiating execution with', selectedApprovers.length, 'approvers');

      // Get ContractDocuments drive ID
      let driveId = contract.DriveId;

      if (!driveId) {
        console.log('[ContractDetail] DriveId not found, fetching ContractDocuments drive...');
        try {
          // Find ContractDocuments drive (same method used elsewhere in app)
          const drives = await service.getDrives();
          const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");

          if (!docsDrive) {
            throw new Error('ContractDocuments drive not found');
          }

          driveId = docsDrive.id;
          console.log('[ContractDetail] ✅ Got ContractDocuments drive ID:', driveId);

          // Auto-save DriveId to contract for future use
          await service.updateListItem("Contracts", id, { DriveId: driveId });
          console.log('[ContractDetail] ✅ Saved DriveId to contract');
        } catch (driveError) {
          console.error('[ContractDetail] ❌ Failed to get drive ID:', driveError);
          addNotification("Could not find ContractDocuments drive. Please verify it exists in SharePoint.", "error");
          setExecutionInProgress(false);
          return;
        }
      }

      // Build folder path using actual contract structure: entity/department/year/month/refno
      const contractFolder = buildFolderPath(
        contract.Entity,
        contract.Department,
        contract.Year,
        contract.Month,
        contract.RefNo
      );

      console.log('[ContractDetail] DriveId:', driveId);
      console.log('[ContractDetail] Entity:', contract.Entity);
      console.log('[ContractDetail] Department:', contract.Department);
      console.log('[ContractDetail] Year:', contract.Year);
      console.log('[ContractDetail] Month:', contract.Month);
      console.log('[ContractDetail] RefNo:', contract.RefNo);
      console.log('[ContractDetail] Built contractFolder:', contractFolder);

      // Fetch folder contents to find the contract draft document
      let contractDraftPath = null;
      try {
        const folderContents = await service.getFolderContents(driveId, contractFolder);
        const allDocs = (folderContents?.value || []).filter(i => !i.folder);

        // Get metadata to identify document types
        let metadata = [];
        try {
          const metaData = await service.getListItems("FileID");
          metadata = (metaData?.value || []).map(m => ({
            FileID: m.fields?.FileID,
            DocumentType: m.fields?.DocumentType,
            IsCraf: m.fields?.IsCraf
          }));
        } catch (e) {
          console.warn('[ContractDetail] FileID list not found, using filename detection');
        }

        // Find contract draft document — exclude the CRAF draft, which is also
        // tagged "contract_draft" so it shows in the same tab, but must never be
        // picked up as the main contract's draft when marking for execution.
        const draftDoc = allDocs.find(doc => {
          if (doc.name.toLowerCase().startsWith("craf-")) return false;
          const meta = metadata.find(m => m.FileID === doc.id);
          if (meta?.IsCraf === "Yes") return false;
          const docType = meta?.DocumentType || guessDocType(doc.name);
          return docType === "contract_draft";
        });

        if (draftDoc) {
          contractDraftPath = `${contractFolder}/${draftDoc.name}`;
          console.log('[ContractDetail] Found contract draft:', draftDoc.name);
        } else {
          throw new Error('No contract draft document found in the Contract Draft tab. Please ensure a contract draft is uploaded.');
        }
      } catch (error) {
        console.error('[ContractDetail] Error finding contract draft:', error);
        addNotification('Could not find contract draft document: ' + error.message, 'error');
        setExecutionInProgress(false);
        return;
      }

      console.log('[ContractDetail] contractDraftPath:', contractDraftPath);

      // 1. Create execution workflow (will fail if PDF cannot be generated)
      let execution;
      try {
        const adminEmail = currentUser?.mail || currentUser?.userPrincipalName;
        execution = await executionService.initiateExecution(service, {
          contractId: id,
          contractRef: contract.RefNo,
          contractTitle: contract.Title,
          approvers: selectedApprovers,
          initiatedBy: adminEmail,
          driveId,
          contractDraftPath
        });

        console.log('[ContractDetail] ✅ Execution created:', execution.executionId);
      } catch (execError) {
        console.error('[ContractDetail] ❌ Execution failed:', execError.message);
        addNotification(
          `Failed to initiate execution: ${execError.message}`,
          "error"
        );
        setExecutionInProgress(false);
        return;
      }

      // 2. Update contract status to "Awaiting Execution"
      await service.updateListItem("Contracts", id, {
        Status: "Awaiting Execution"
      });

      // 3. Send notification to first approver
      const firstApprover = selectedApprovers[0];
      const executionLink = window.location.origin + `/approver/${execution.executionId}/${execution.executionId}`;

      const emailBody = `
        <p>Hi ${firstApprover.displayName},</p>
        <p>You have been requested to approve and sign the following contract:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p><strong>Contract:</strong> ${contract.Title}</p>
          <p><strong>Reference:</strong> ${contract.RefNo}</p>
          <p><strong>Approval Order:</strong> 1 of ${selectedApprovers.length}</p>
        </div>
        <p><a href="${executionLink}" style="display: inline-block; padding: 12px 24px; background-color: #dc2626; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">Sign Document</a></p>
        <p>Click the button above to access the document and add your signature.</p>
        <p>Best regards,<br/>LexCore Contract Management System</p>
      `;

      await service.sendEmail([firstApprover.mail], `[LexCore] Contract Awaiting Your Signature: ${contract.RefNo}`, emailBody);

      setShowExecutionModal(false);
      setContract(p=>({...p, Status: "Awaiting Execution"}));
      addNotification(`Execution initiated with ${selectedApprovers.length} approvers. First approver notified.`, "success");
    } catch (error) {
      console.error('[ContractDetail] Error initiating execution:', error);
      addNotification("Error initiating execution: " + error.message, "error");
    } finally {
      setExecutionInProgress(false);
    }
  };

  // ─── CRAF: Generate (Word draft from a chosen template) ───────────────────
  const handleGenerateCraf = async (templateFileName) => {
    if (!driveId || !folderPath) { addNotification("Drive not configured","error"); return; }
    setCrafGenerating(true);
    try {
      const result = await generateCrafDraft({
        service,
        contractData: {
          contractTitle:        contract.Title,
          refNo:                contract.RefNo,
          contractType:         contract.ContractType,
          requestDate:          contract.RequestDate,
          effectiveDate:        contract.EffectiveDate,
          expiryDate:           contract.ExpiryDate,
          duration:             contract.Duration,
          isRenewal:            contract.IsRenewal,
          year:                 contract.Year,
          month:                contract.Month,
          entity:               contract.Entity,
          department:           contract.Department,
          categoryManager:      contract.CategoryManager,
          contractHolder:       contract.ContractHolder,
          vendorName:           contract.VendorName,
          requestedBy:          contract.RequestedBy,
          ceilingValueMonthly:  contract.CeilingValueMonthly,
          ceilingValueAnnual:   contract.CeilingValueAnnual,
          deviationFromPayment: contract.DeviationFromPayment,
          parentContractRef:    contract.ParentContractRef,
          additionalComments:   contract.Comments,
        },
        folderPath,
        templateFileName,
        outputFileName: getCrafDraftFileName(contract.RefNo),
      });

      // Tag the generated file so it lands in the Contract Draft tab alongside the main draft
      if (result?.fileId) {
        try {
          await service.createListItem("FileID", {
            FileID: result.fileId,
            FilePath: result.fileName,
            DocumentType: "contract_draft",
            IsCraf: "Yes",
            CreatedDate: new Date().toISOString(),
          });
        } catch (e) {
          console.warn("[ContractDetail] Could not tag CRAF draft metadata:", e);
        }
      }

      setShowCrafTemplateModal(false);
      setCrafDraftExists(true);
      setDocsKey(k => k + 1);
      addNotification("CRAF document generated. Open it from the Contract Draft tab to review before sending for execution.", "success");
    } catch (e) {
      addNotification("Failed to generate CRAF: " + e.message, "error");
    } finally {
      setCrafGenerating(false);
    }
  };

  // ─── CRAF: Send for Execution (Word → PDF, sequential approver signing) ───
  const handleInitiateCrafExecution = async (selectedApprovers) => {
    setCrafExecutionInProgress(true);
    try {
      let driveIdForCraf = contract.DriveId || driveId;
      if (!driveIdForCraf) {
        const drives = await service.getDrives();
        const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");
        if (!docsDrive) throw new Error('ContractDocuments drive not found');
        driveIdForCraf = docsDrive.id;
      }

      const crafDraftFileName = getCrafDraftFileName(contract.RefNo);
      const crafDraftPath = `${folderPath}/${crafDraftFileName}`;

      // Confirm the CRAF draft actually exists before attempting conversion
      const folderContents = await service.getFolderContents(driveIdForCraf, folderPath);
      const draftExists = (folderContents?.value || []).some(f => !f.folder && f.name.toLowerCase() === crafDraftFileName.toLowerCase());
      if (!draftExists) {
        throw new Error('CRAF draft document not found. Generate the CRAF document first.');
      }

      const execution = await crafExecutionService.initiateCrafExecution(service, {
        contractId: id,
        contractRef: contract.RefNo,
        contractTitle: contract.Title,
        approvers: selectedApprovers,
        initiatedBy: currentUser?.mail || currentUser?.userPrincipalName,
        driveId: driveIdForCraf,
        crafDraftPath
      });

      const firstApprover = selectedApprovers[0];
      const executionLink = window.location.origin + `/craf-approver/${execution.executionId}/${execution.executionId}`;

      const emailBody = `
        <p>Hi ${firstApprover.displayName},</p>
        <p>You have been requested to review and sign the CRAF (Contract Review and Approval Form) for the following contract:</p>
        <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p><strong>Contract:</strong> ${contract.Title}</p>
          <p><strong>Reference:</strong> ${contract.RefNo}</p>
          <p><strong>Approval Order:</strong> 1 of ${selectedApprovers.length}</p>
        </div>
        <p><a href="${executionLink}" style="display: inline-block; padding: 12px 24px; background-color: #dc2626; color: white; text-decoration: none; border-radius: 6px; font-weight: bold;">Sign CRAF</a></p>
        <p>Click the button above to access the CRAF document and add your signature.</p>
        <p>Best regards,<br/>LexCore Contract Management System</p>
      `;

      await service.sendEmail([firstApprover.mail], `[LexCore] CRAF Awaiting Your Signature: ${contract.RefNo}`, emailBody);

      setShowCrafExecutionModal(false);
      setDocsKey(k => k + 1);
      await loadCrafStatus();
      addNotification(`CRAF execution initiated with ${selectedApprovers.length} approver(s). First approver notified.`, "success");
    } catch (error) {
      console.error('[ContractDetail] Error initiating CRAF execution:', error);
      addNotification("Error initiating CRAF execution: " + error.message, "error");
    } finally {
      setCrafExecutionInProgress(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus||newStatus===contract.Status) return;
    const oldStatus=contract.Status;
    try {
      const updates={Status:newStatus};
      if(newStatus==="Executed") updates.ExecutionDate=new Date().toISOString();
      if(["Executed","Approved"].includes(newStatus)&&contract.RequestDate) updates.CycleDays=Math.ceil((Date.now()-new Date(contract.RequestDate))/86400000);
      await service.updateListItem("Contracts",id,updates);
      setContract(p=>({...p,Status:newStatus,...updates}));
      const html=buildEmailHTML("statusUpdate",{title:contract.Title,refNo:contract.RefNo,oldStatus,newStatus,portalUrl:window.location.href});
      [contract.RequestedBy].filter(Boolean).forEach(e=>service.sendEmail(e,"[LexCore] Status Update: "+contract.RefNo,html).catch(()=>{}));
      addNotification("Status updated to: "+newStatus,"success");

      // On approval, convert all Word documents in the contract folder to PDF
      if(newStatus==="Approved" && driveId && folderPath) {
        addNotification("Converting documents to PDF...","info");
        try {
          const folderContents = await service.getFolderContents(driveId, folderPath);
          const docxFiles = (folderContents?.value||[]).filter(i =>
            !i.folder && /\.(docx?|doc)$/i.test(i.name)
          );
          let converted = 0;
          for(const file of docxFiles) {
            try {
              const pdfBuffer = await service.downloadFileAsPdf(driveId, file.id);
              const pdfName = file.name.replace(/\.(docx?|doc)$/i, ".pdf");
              await service.uploadFile(driveId, folderPath, pdfName, pdfBuffer, "application/pdf");
              await service.deleteFile(driveId, file.id);
              converted++;
            } catch(convErr) {
              console.warn("PDF conversion failed for", file.name, convErr);
            }
          }
          if(converted > 0) addNotification(`${converted} document${converted>1?"s":""} converted to PDF`,"success");
          else addNotification("No Word documents found to convert","info");
        } catch(pdfErr) {
          addNotification("PDF conversion error: "+pdfErr.message,"error");
        }
      }
    } catch(e){ addNotification("Error: "+e.message,"error"); }
  };

  const generateReviewerToken = (contractId) => {
    const date = new Date().toISOString().split("T")[0];
    return btoa(contractId + "_reviewer_" + date).substring(0, 12);
  };

  const openEdit = () => {
    setEditForm({
      Title: contract.Title||"", RefNo: contract.RefNo||"",
      Entity: contract.Entity||"", Department: contract.Department||"",
      ContractType: contract.ContractType||"", VendorName: contract.VendorName||"",
      CategoryManager: contract.CategoryManager||"", ContractOwner: contract.ContractOwner||"",
      EffectiveDate: contract.EffectiveDate?contract.EffectiveDate.slice(0,10):"",
      ExpiryDate: contract.ExpiryDate?contract.ExpiryDate.slice(0,10):"",
      RequestDate: contract.RequestDate?contract.RequestDate.slice(0,10):"",
      Duration: contract.Duration||"", IsRenewal: contract.IsRenewal||"No",
      CeilingValueMonthly: contract.CeilingValueMonthly||"",
      CeilingValueAnnual: contract.CeilingValueAnnual||"",
      DeviationFromPayment: contract.DeviationFromPayment||"No",
      ProcurementType: contract.ProcurementType||"",
      Comments: contract.Comments||"",
      ...(()=>{ try { const sp = contract.SplitPayment ? JSON.parse(contract.SplitPayment) : null;
        return { splitPayment: !!sp?.currency1, Currency1: sp?.currency1||"USD", Currency2: sp?.currency2||"NGN",
          SplitPercent1: sp?.percent1||"", SplitPercent2: sp?.percent2||"",
          SplitAmount1: sp?.amount1||"", SplitAmount2: sp?.amount2||"" };
      } catch { return { splitPayment: false, Currency1:"USD", Currency2:"NGN", SplitPercent1:"", SplitPercent2:"", SplitAmount1:"", SplitAmount2:"" }; } })(),
    });
    setShowEdit(true);
  };

  const handleEditSave = async () => {
    if (!editForm.Title||!editForm.Entity||!editForm.VendorName||!editForm.ContractType||!editForm.Department||!editForm.EffectiveDate) {
      addNotification("Please fill all required fields","error"); return;
    }
    setEditSaving(true);
    try {
      const updates = {
        Title: editForm.Title, RefNo: editForm.RefNo,
        Entity: editForm.Entity, Department: editForm.Department,
        ContractType: editForm.ContractType, VendorName: editForm.VendorName,
        CategoryManager: editForm.CategoryManager, ContractHolder: editForm.ContractHolder,
        EffectiveDate: editForm.EffectiveDate||null,
        ExpiryDate: editForm.ExpiryDate||null, RequestDate: editForm.RequestDate||null,
        Duration: editForm.Duration, IsRenewal: editForm.IsRenewal,
        CeilingValueMonthly: editForm.CeilingValueMonthly,
        CeilingValueAnnual: editForm.CeilingValueAnnual,
        DeviationFromPayment: editForm.DeviationFromPayment,
        ProcurementType: editForm.ProcurementType,
        Comments: editForm.Comments,
        SplitPayment: editForm.splitPayment
          ? JSON.stringify({ currency1: editForm.Currency1, percent1: editForm.SplitPercent1, amount1: editForm.SplitAmount1,
                             currency2: editForm.Currency2, percent2: editForm.SplitPercent2, amount2: editForm.SplitAmount2 })
          : null,
      };
      await service.updateListItem("Contracts", id, updates);
      setContract(p => ({...p, ...updates}));
      setShowEdit(false);
      addNotification("Contract updated successfully","success");
    } catch(e){ addNotification("Save failed: "+e.message,"error"); }
    finally { setEditSaving(false); }
  };

  const [regenDeleteExisting, setRegenDeleteExisting] = useState(false);

  const handleRegenerate = async () => {
    if (!driveId || !folderPath) { addNotification("Drive not configured","error"); return; }
    setRegenRunning(true);
    setRegenLog([]);
    const log = [];

    try {
      // 1. Optionally delete existing draft documents
      if (regenDeleteExisting) {
        const folderContents = await service.getFolderContents(driveId, folderPath).catch(() => ({ value: [] }));
        const allFiles = (folderContents?.value || []).filter(i => !i.folder);

        const safeRef = (contract.RefNo || "").replace(/[/\\]/g, "-").toLowerCase();
        const draftFiles = allFiles.filter(f => {
          const n = f.name.toLowerCase();
          if (n.startsWith("craf-")) return false; // never touch the CRAF draft here
          return n === safeRef + ".docx" || (n.includes(safeRef) && n.endsWith(".docx"));
        });

        log.push({ text: `Found ${draftFiles.length} existing draft(s)`, type: "info" });
        setRegenLog([...log]);

        for (const file of draftFiles) {
          try {
            await service.deleteFile(driveId, file.id);
            log.push({ text: `Deleted: ${file.name}`, type: "ok" });
            setRegenLog([...log]);
          } catch (delErr) {
            const msg = String(delErr?.message || delErr);
            const isLocked = msg.includes("423") || msg.toLowerCase().includes("lock") || msg.toLowerCase().includes("checked out");
            const isGone   = msg.includes("404") || msg.toLowerCase().includes("not found");
            const reason   = isLocked ? "File is open/checked out — close it in Word or browser first"
                           : isGone   ? "Already removed"
                           : msg;
            log.push({ text: `Could not delete "${file.name}": ${reason}`, type: isGone ? "info" : "error" });
            setRegenLog([...log]);
            if (isLocked) {
              addNotification("Document is open — close it and try again","error");
              setRegenRunning(false);
              return;
            }
          }
        }
      } else {
        log.push({ text: "Skipping deletion — existing documents will be kept", type: "info" });
        setRegenLog([...log]);
      }

      // 2. Generate new draft from template
      log.push({ text: "Generating new document from template…", type: "info" });
      setRegenLog([...log]);

      const result = await generateContractDraft({
        service,
        contractData: {
          contractTitle:        contract.Title,
          refNo:                contract.RefNo,
          contractType:         contract.ContractType,
          requestDate:          contract.RequestDate,
          effectiveDate:        contract.EffectiveDate,
          expiryDate:           contract.ExpiryDate,
          duration:             contract.Duration,
          isRenewal:            contract.IsRenewal,
          year:                 contract.Year,
          month:                contract.Month,
          entity:               contract.Entity,
          department:           contract.Department,
          categoryManager:      contract.CategoryManager,
          contractHolder:       contract.ContractHolder,
          vendorName:           contract.VendorName,
          requestedBy:          contract.RequestedBy,
          ceilingValueMonthly:  contract.CeilingValueMonthly,
          ceilingValueAnnual:   contract.CeilingValueAnnual,
          deviationFromPayment: contract.DeviationFromPayment,
          parentContractRef:    contract.ParentContractRef,
          additionalComments:   contract.Comments,
        },
        folderPath,
      });

      if (!result) {
        log.push({ text: `No active template mapped for "${contract.ContractType}" — set one up in Template Library`, type: "error" });
        setRegenLog([...log]);
        addNotification("No template found for this contract type","error");
        setRegenRunning(false);
        return;
      }

      log.push({ text: `Done — new document: ${result.fileName}`, type: "ok" });
      setRegenLog([...log]);
      addNotification("Contract document regenerated successfully","success");
      setDocsKey(k => k + 1);
    } catch (e) {
      const msg = e?.message || String(e);
      log.push({ text: "Unexpected error: " + msg, type: "error" });
      setRegenLog([...log]);
      addNotification("Regeneration failed: " + msg,"error");
    }
    setRegenRunning(false);
  };

  if(loading) return (<div className="flex flex-col items-center justify-center h-96"><div className="w-10 h-10 border-3 border-light-border dark:border-dark-border border-t-yellow-600 rounded-full animate-spin"/></div>);
  if(!contract) return <div className="text-center py-16 text-light-text-secondary dark:text-dark-text-secondary">Contract not found.</div>;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-1.5 mb-4 text-sm">
        <button onClick={()=>navigate("/contracts")} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer p-0 hover:text-light-text dark:hover:text-dark-text">Contracts</button>
        <span className="text-light-border dark:text-dark-border">{"--->"}</span>
        <span className="text-accent font-mono">{contract.RefNo}</span>
      </div>
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-medium text-light-text dark:text-dark-text mb-2">{contract.Title||"Untitled Contract"}</h1>
          <div className="flex gap-2 flex-wrap items-center">
            <span className="font-mono text-sm text-accent">{contract.RefNo}</span>
            <span className="text-light-border dark:text-dark-border">-</span>
            <span className="px-2.5 py-0.5 rounded-full text-sm font-medium" style={{backgroundColor: getStatusColor(contract.Status)+"22", color: getStatusColor(contract.Status)}}>{contract.Status}</span>
            <span className="text-light-border dark:text-dark-border">-</span>
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{contract.Entity}</span>
            <span className="text-light-border dark:text-dark-border">-</span>
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{contract.ContractType}</span>
          </div>
        </div>
        {canEdit && contract.Status !== "Close Out" && (
          <div className="flex gap-2.5 flex-wrap">
            {isAdmin&&<button onClick={()=>setShowRegen(true)} className="px-4.5 py-2.25 bg-transparent border border-purple-600 rounded-lg text-purple-600 cursor-pointer text-sm font-medium hover:bg-purple-600/10 transition-colors">Regenerate Doc</button>}
            <button onClick={openEdit} className="px-4.5 py-2.25 bg-transparent border border-yellow-600 rounded-lg text-yellow-600 cursor-pointer text-sm font-medium hover:bg-yellow-600/10 transition-colors">Edit Details</button>
            {isAdmin&&<button onClick={()=>setShowShare(true)} className="px-4.5 py-2.25 bg-blue-600 border-none rounded-lg text-white cursor-pointer text-sm font-medium hover:bg-blue-700 transition-colors">Share Contract</button>}
            {isAdmin && contract.Status !== "Awaiting Execution" && contract.Status !== "Executed" && contract.Status !== "Close Out" && <button onClick={()=>setShowExecutionModal(true)} className="px-4.5 py-2.25 bg-green-600 border-none rounded-lg text-white cursor-pointer text-sm font-medium hover:bg-green-700 transition-colors">Mark for Execution</button>}
            {isAdmin && (
              <button onClick={()=>setShowCrafTemplateModal(true)} disabled={crafGenerating} className="px-4.5 py-2.25 bg-transparent border border-indigo-500 rounded-lg text-indigo-400 cursor-pointer text-sm font-medium hover:bg-indigo-500/10 transition-colors disabled:opacity-50">
                {crafGenerating ? "Generating CRAF..." : crafDraftExists ? "Regenerate CRAF" : "Generate CRAF"}
              </button>
            )}
            {isAdmin && crafDraftExists && crafExecution?.Status !== "In Progress" && (
              <button onClick={()=>setShowCrafExecutionModal(true)} className="px-4.5 py-2.25 bg-indigo-600 border-none rounded-lg text-white cursor-pointer text-sm font-medium hover:bg-indigo-700 transition-colors">
                Send CRAF for Execution
              </button>
            )}
            {isAdmin && crafExecution?.Status === "In Progress" && (
              <span className="px-4.5 py-2.25 bg-indigo-600/15 border border-indigo-600/30 rounded-lg text-indigo-400 text-sm font-medium">
                CRAF Execution In Progress ({crafExecution.ApproversCompleted||0}/{crafExecution.TotalApprovers||0})
              </span>
            )}
          </div>
        )}
      </div>
      <div className="h-0.5 bg-gradient-to-r from-yellow-600 to-transparent my-3"/>

      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6">
        <div className="flex flex-col gap-5">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-6">
            <div className="font-serif text-lg font-medium text-accent mb-3 pb-2 border-b border-light-border dark:border-dark-border">Contract Information</div>
            <InfoRow label="Contract Type" value={contract.ProcurementType}/>
            <InfoRow label="Reference Number" value={<span className="font-mono text-accent">{contract.RefNo}</span>}/>
            <InfoRow label="Contract Title" value={contract.Title}/>
            <InfoRow label="Contract Template" value={contract.ContractType}/>
            {contract.ContractType && contract.ContractType.includes("Addendum") && contract.ParentContractRef && (
              <InfoRow label="Parent Contract" value={
                <button onClick={()=>navigate("/contracts/"+contract.ParentContractId)} className="bg-none border-none p-0 cursor-pointer font-mono text-sm text-accent underline hover:opacity-80">
                  {contract.ParentContractRef}
                </button>
              }/>
            )}
            <InfoRow label="Contracting Entity" value={contract.Entity}/>
            <InfoRow label="User Department" value={contract.Department}/>
            <InfoRow label="Supplier" value={contract.VendorName}/>
            <InfoRow label="Category Manager" value={contract.CategoryManager}/>
            <InfoRow label="Contract Owner" value={contract.ContractOwner}/>
            <InfoRow label="Effective Date" value={contract.EffectiveDate?new Date(contract.EffectiveDate).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"}):""}/>
            <InfoRow label="Expiry Date" value={contract.ExpiryDate?new Date(contract.ExpiryDate).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"}):""}/>
            <InfoRow label="Duration" value={contract.Duration}/>
            <InfoRow label="Renewable" value={contract.IsRenewal}/>
            <InfoRow label="Ceiling (Monthly)" value={contract.CeilingValueMonthly}/>
            <InfoRow label="Ceiling (Annual)" value={contract.CeilingValueAnnual}/>
            <InfoRow label="Payment Deviation" value={contract.DeviationFromPayment}/>
            <InfoRow label="Contract Type" value={contract.ProcurementType}/>
            {(()=>{ try { const sp = contract.SplitPayment ? JSON.parse(contract.SplitPayment) : null; if (!sp?.currency1) return null;
              return <InfoRow label="Split Payment" value={<div className="flex flex-col gap-1">
                <span><span className="font-mono text-accent">{sp.currency1}</span> — {sp.percent1}% {sp.amount1 ? `· ${sp.amount1}` : ""}</span>
                <span><span className="font-mono text-accent">{sp.currency2}</span> — {sp.percent2}% {sp.amount2 ? `· ${sp.amount2}` : ""}</span>
              </div>}/>;
            } catch { return null; } })()}
            <InfoRow label="Request Date" value={contract.RequestDate?new Date(contract.RequestDate).toLocaleDateString("en-GB"):""}/>
            <InfoRow label="Execution Date" value={contract.ExecutionDate?new Date(contract.ExecutionDate).toLocaleDateString("en-GB"):""}/>
            <InfoRow label="Cycle Time" value={contract.CycleDays?<span className={`font-mono text-sm ${parseInt(contract.CycleDays)>30?"text-red-600":"text-green-600"}`}>{contract.CycleDays} days</span>:null}/>
            {contract.Comments&&<InfoRow label="Comments" value={<span className="text-light-text-secondary dark:text-dark-text-secondary leading-relaxed block">{contract.Comments}</span>}/>}
          </div>
          <DocumentsSection key={docsKey} contractId={id} contractRef={contract.RefNo} folderPath={folderPath} driveId={driveId} isAdmin={isAdmin} service={service} addNotification={addNotification} currentUser={currentUser} isCategoryManager={isCategoryManager}/>
          <ObligationsList contractId={id} contractRefNo={contract.RefNo} canEdit={isAdmin || contract.CategoryManager === currentUser?.mail} isAdmin={isAdmin}/>
          {!(contract.ContractType && contract.ContractType.includes("Addendum")) && (
            <AddendaSection contractId={id} contractRef={contract.RefNo} service={service} navigate={navigate}/>
          )}
        </div>

        <div className="flex flex-col gap-4">
          {isAdmin&&(
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-5">
              <div className="font-serif text-base font-medium mb-3.5 text-accent">Update Status</div>
              <div className="mb-3">
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-mono">Current</div>
                <span className="inline-block px-3 py-0.5 rounded-full text-sm font-semibold" style={{backgroundColor: getStatusColor(contract.Status)+"22", color: getStatusColor(contract.Status)}}>{contract.Status}</span>
              </div>
              <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1.5 font-mono">New Status</div>
              <select value={newStatus} onChange={e=>setNewStatus(e.target.value)} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm mb-3 outline-none focus:ring-1 focus:ring-accent">
                {Object.values(CONTRACT_STATUSES).map(s=><option key={s}>{s}</option>)}
              </select>
              <button onClick={handleStatusUpdate} disabled={!newStatus||newStatus===contract.Status}
                className={`w-full px-3 py-2.5 border-none rounded-lg font-semibold text-sm transition-colors ${newStatus!==contract.Status ? "bg-yellow-600 text-slate-900 cursor-pointer hover:bg-yellow-700" : "bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed"}`}>
                Update & Notify
              </button>
            </div>
          )}

          {contract.Status === "Requested" && (
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-5">
              <div className="font-serif text-base font-medium mb-3.5 text-accent">Ready for Processing</div>
              <div className="mb-3">
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1.25">Current Status</div>
                <span className={`inline-block px-3 py-0.5 rounded-full text-sm font-semibold ${contract.IsReady==="Yes"?"bg-green-600/20 text-green-600":"bg-orange-600/20 text-orange-600"}`}>{contract.IsReady==="Yes"?"✅ Ready":"⚠️ Incomplete"}</span>
              </div>
              <button onClick={toggleReady} disabled={updatingReady} className={`w-full px-3 py-2.5 border-none rounded-lg font-semibold text-sm transition-colors ${contract.IsReady==="Yes"?"bg-orange-600/20 text-orange-600 hover:bg-orange-600/30":"bg-green-600/20 text-green-600 hover:bg-green-600/30"} ${updatingReady?"opacity-60 cursor-not-allowed":"cursor-pointer"}`}>
                {updatingReady?"Updating...":contract.IsReady==="Yes"?"Mark as Incomplete":"Mark as Ready"}
              </button>
            </div>
          )}

          {contract.Status === "In Review" && (
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-5">
              <div className="font-serif text-base font-medium mb-3.5 text-accent">Review Status</div>
              {loadingReviewers ? (
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">Loading reviewers...</div>
              ) : reviewers.length === 0 ? (
                <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No reviewers assigned</div>
              ) : (
                <div className="flex flex-col gap-3">
                  {reviewers.map((reviewer) => (
                    <div key={reviewer.id} className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-sm font-medium text-light-text dark:text-dark-text">{reviewer.name}</div>
                          <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{reviewer.email}</div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold whitespace-nowrap ${
                          reviewer.status === "Complete"
                            ? "bg-green-600/20 text-green-600"
                            : reviewer.status === "In Progress"
                            ? "bg-blue-600/20 text-blue-600"
                            : "bg-gray-600/20 text-gray-600"
                        }`}>
                          {reviewer.status === "Complete" ? "✅ Completed" : reviewer.status === "In Progress" ? "⏳ In Progress" : "⏳ Pending"}
                        </span>
                      </div>
                      <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary space-y-1">
                        {reviewer.createdAt && (
                          <div>📅 Received: {new Date(reviewer.createdAt).toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                        )}
                        {reviewer.completedAt && (
                          <div>✓ Completed: {new Date(reviewer.completedAt).toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-5">
            <div className="font-serif text-base font-medium mb-3.5">Document Types</div>
            <div className="flex flex-col gap-1.25">
              {DOC_TYPES_WITH_EMOJI.map(t=>(
                <div key={t.id} className="flex items-center gap-2 py-1.25 border-b border-light-border dark:border-dark-border">
                  <div className="w-0.75 h-3.5 rounded-sm flex-shrink-0" style={{backgroundColor:t.color}}/>
                  <span className="flex-1 text-sm text-light-text-secondary dark:text-dark-text-secondary">{t.emoji} {t.label}</span>
                </div>
              ))}
            </div>
            {isAdmin&&<div className="mt-3.5 px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">Add documents of any type at any time using the <span className="text-accent">+ Add Document</span> button.</div>}
          </div>

          {folderPath&&(
            <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-5">
              <div className="font-serif text-base font-medium mb-2">Storage Location</div>
              <div className="font-mono text-xs text-accent px-2.5 py-2 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded break-all leading-relaxed">
                {folderPath.split("/").join(" / ")}
              </div>
            </div>
          )}
        </div>
      </div>

      {showRegen&&(
        <div className="fixed inset-0 bg-black/82 flex items-center justify-center z-[400] p-4">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-3xl w-full max-w-lg flex flex-col shadow-2xl">
            <div className="px-7 py-5 border-b border-light-border dark:border-dark-border flex justify-between items-center">
              <div>
                <div className="font-serif text-2xl font-medium text-light-text dark:text-dark-text">Regenerate Contract Document</div>
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5 font-mono">{contract.RefNo}</div>
              </div>
              {!regenRunning&&<button onClick={()=>{setShowRegen(false);setRegenLog([]);setRegenDeleteExisting(false);}} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-2xl hover:text-light-text dark:hover:text-dark-text">×</button>}
            </div>
            <div className="px-7 py-6 flex flex-col gap-4">
              <div className="px-4 py-3.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-2xl text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
                A new draft document will be generated from the template mapped to <span className="text-accent font-mono">{contract.ContractType}</span> and uploaded to the contract folder.
              </div>
              {/* Optional: delete existing */}
              <label className={`flex items-start gap-3 cursor-pointer px-4 py-3 border rounded-2xl transition-all ${regenDeleteExisting?"bg-yellow-600/5 border-yellow-600/25":"border-light-border dark:border-dark-border"}`}>
                <input type="checkbox" checked={regenDeleteExisting} onChange={e=>setRegenDeleteExisting(e.target.checked)}
                  className="mt-0.5 w-4 h-4 flex-shrink-0 cursor-pointer accent-yellow-600"/>
                <div>
                  <div className="text-sm font-medium text-light-text dark:text-dark-text">Delete existing draft document</div>
                  <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">Remove the current <span className="font-mono">.docx</span> draft before uploading the new one. Leave unchecked to keep both versions.</div>
                </div>
              </label>
              {regenDeleteExisting&&(
                <div className="px-3.5 py-2.5 bg-red-600/10 border border-red-600/25 rounded-lg text-xs text-red-600">
                  If the existing document is currently open in Word or a browser, deletion will fail. Close it first.
                </div>
              )}
              {/* Progress log */}
              {regenLog.length>0&&(
                <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-2xl px-3.5 py-3 flex flex-col gap-1.25 max-h-44 overflow-y-auto">
                  {regenLog.map((entry,i)=>(
                    <div key={i} className={`flex items-start gap-2 text-xs font-mono ${entry.type==="ok"?"text-green-600":entry.type==="error"?"text-red-600":"text-light-text-secondary dark:text-dark-text-secondary"}`}>
                      <span className="flex-shrink-0">{entry.type==="ok"?"✓":entry.type==="error"?"✕":"·"}</span>
                      <span className="leading-relaxed">{entry.text}</span>
                    </div>
                  ))}
                  {regenRunning&&<div className="flex items-center gap-2 text-xs text-accent font-mono"><div className="w-2.5 h-2.5 border-2 border-accent/25 border-t-accent rounded-full animate-spin flex-shrink-0"/><span>Working…</span></div>}
                </div>
              )}
            </div>
            <div className="px-7 py-4 border-t border-light-border dark:border-dark-border flex gap-2.5 justify-end">
              <button onClick={()=>{setShowRegen(false);setRegenLog([]);setRegenDeleteExisting(false);}} disabled={regenRunning}
                className={`px-5.5 py-2.5 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm ${regenRunning?"opacity-50 cursor-not-allowed":"hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary"}`}>
                {regenLog.some(l=>l.type==="ok"&&l.text.startsWith("Done"))?"Close":"Cancel"}
              </button>
              <button onClick={handleRegenerate} disabled={regenRunning||regenLog.some(l=>l.text.startsWith("Done"))}
                className={`px-7 py-2.5 border-none rounded-lg text-sm font-semibold ${regenRunning||regenLog.some(l=>l.text.startsWith("Done"))?"bg-light-border dark:bg-dark-border text-light-text-secondary dark:text-dark-text-secondary cursor-not-allowed":"bg-purple-600 text-white cursor-pointer hover:bg-purple-700"}`}>
                {regenRunning?"Generating…":regenLog.some(l=>l.text.startsWith("Done"))?"Done":"Generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit&&(
        <div className="fixed inset-0 bg-black/82 flex items-center justify-center z-[400] p-4">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            {/* Modal header */}
            <div className="px-7 py-5 border-b border-light-border dark:border-dark-border flex justify-between items-center flex-shrink-0">
              <div>
                <div className="font-serif text-2xl font-medium text-light-text dark:text-dark-text">Edit Contract Details</div>
                <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5 font-mono">{contract.RefNo}</div>
              </div>
              <button onClick={()=>setShowEdit(false)} className="bg-none border-none text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-2xl hover:text-light-text dark:hover:text-dark-text leading-none">×</button>
            </div>
            {/* Scrollable body */}
            <div className="overflow-y-auto px-7 py-6 flex flex-col gap-5">
              {/* Core fields */}
              <div className="grid grid-cols-2 gap-3.5">
                {[["Contract Title *","Title","text"],["Contract Ref No. *","RefNo","text"]].map(([label,key,type])=>(
                  <div key={key}>
                    <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">{label}</label>
                    <input type={type} value={editForm[key]||""} onChange={e=>setEditForm(p=>({...p,[key]:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"/>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3.5">
                <div>
                  <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">Contracting Entity *</label>
                  <select value={editForm.Entity||""} onChange={e=>setEditForm(p=>({...p,Entity:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent">
                    <option value="">Select…</option>
                    {ENTITIES.map(e=><option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">Contract Type *</label>
                  <select value={editForm.ContractType||""} onChange={e=>setEditForm(p=>({...p,ContractType:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent">
                    <option value="">Select…</option>
                    {contractTypes.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">Supplier / Vendor *</label>
                  <input value={editForm.VendorName||""} onChange={e=>setEditForm(p=>({...p,VendorName:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"/>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3.5">
                {[["Category Manager","CategoryManager"],["Contract Owner","ContractOwner"]].map(([label,key])=>(
                  <div key={key}>
                    <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">{label}</label>
                    <input value={editForm[key]||""} onChange={e=>setEditForm(p=>({...p,[key]:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"/>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3.5">
                {[["Effective Date *","EffectiveDate"],["Expiry Date","ExpiryDate"],["Request Date","RequestDate"]].map(([label,key])=>(
                  <div key={key}>
                    <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">{label}</label>
                    <input type="date" value={editForm[key]||""} onChange={e=>setEditForm(p=>({...p,[key]:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"/>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3.5">
                {[["Ceiling Value (Monthly)","CeilingValueMonthly"],["Ceiling Value (Annual)","CeilingValueAnnual"],["Duration","Duration"],["Department *","Department"]].map(([label,key])=>(
                  <div key={key}>
                    <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">{label}</label>
                    <input value={editForm[key]||""} onChange={e=>setEditForm(p=>({...p,[key]:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"/>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-2 font-medium">Renewable</label>
                  <div className="flex gap-5">
                    {["Yes","No"].map(v=>(
                      <label key={v} className="flex items-center gap-1.5 text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm">
                        <input type="radio" checked={editForm.IsRenewal===v} onChange={()=>setEditForm(p=>({...p,IsRenewal:v}))} className="accent-accent"/>
                        {v}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-2 font-medium">Deviation from Payment Terms</label>
                  <div className="flex gap-5">
                    {["Yes","No"].map(v=>(
                      <label key={v} className="flex items-center gap-1.5 text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm">
                        <input type="radio" checked={editForm.DeviationFromPayment===v} onChange={()=>setEditForm(p=>({...p,DeviationFromPayment:v}))} className="accent-accent"/>
                        {v}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">Contract Type</label>
                <select value={editForm.ProcurementType||""} onChange={e=>setEditForm(p=>({...p,ProcurementType:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent">
                  <option value="">Select…</option>
                  {["One-off Service","Call-out Service","Framework Order"].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <div className={`flex items-center gap-2.5 ${editForm.splitPayment?"mb-3.5":""}`}>
                  <label className="text-xs text-light-text-secondary dark:text-dark-text-secondary font-medium">Split Payment Currency</label>
                  <div onClick={()=>setEditForm(p=>({...p,splitPayment:!p.splitPayment}))}
                    className={`w-10 h-5.5 rounded-full cursor-pointer position-relative transition-colors flex-shrink-0 flex items-center ${editForm.splitPayment?"bg-yellow-600":"bg-light-border dark:bg-dark-border"}`}>
                    <div className={`w-4 h-4 rounded-full bg-light-text dark:bg-dark-text transition-transform ${editForm.splitPayment?"translate-x-5":"translate-x-0.5"}`}/>
                  </div>
                </div>
                {editForm.splitPayment&&(
                  <div className="grid grid-cols-2 gap-3.5">
                    {[["Currency 1","Currency1"],["Currency 2","Currency2"]].map(([label,key])=>(
                      <div key={key}>
                        <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25">{label}</label>
                        <select value={editForm[key]||""} onChange={e=>setEditForm(p=>({...p,[key]:e.target.value}))} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent">
                          {["USD","NGN","GBP","EUR","ZAR","CAD","AED"].map(c=><option key={c}>{c}</option>)}
                        </select>
                      </div>
                    ))}
                    {[["Split % (Currency 1)","SplitPercent1"],["Split % (Currency 2)","SplitPercent2"],["Amount (Currency 1)","SplitAmount1"],["Amount (Currency 2)","SplitAmount2"]].map(([label,key])=>(
                      <div key={key}>
                        <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25">{label}</label>
                        <input value={editForm[key]||""} onChange={e=>setEditForm(p=>({...p,[key]:e.target.value}))} placeholder={key.includes("Percent")?"e.g. 60":"e.g. 500,000"} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"/>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-light-text-secondary dark:text-dark-text-secondary mb-1.25 font-medium">Comments</label>
                <textarea value={editForm.Comments||""} onChange={e=>setEditForm(p=>({...p,Comments:e.target.value}))} rows={3} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent resize-vertical"/>
              </div>
            </div>
            {/* Footer */}
            <div className="px-7 py-4 border-t border-light-border dark:border-dark-border flex gap-2.5 justify-end flex-shrink-0">
              <button onClick={()=>setShowEdit(false)} className="px-5.5 py-2.5 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary">Cancel</button>
              <button onClick={handleEditSave} disabled={editSaving} className={`px-7 py-2.5 border-none rounded-lg text-slate-900 text-sm font-semibold ${editSaving?"bg-yellow-700 cursor-not-allowed opacity-75":"bg-yellow-600 cursor-pointer hover:bg-yellow-700"}`}>
                {editSaving?"Saving…":"Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showShare&&(
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-300">
          <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif text-2xl font-medium mb-5">Share Contract</h3>

            {/* Share Mode Selection */}
            <div className="mb-6 p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg">
              <label className="text-sm text-light-text-secondary dark:text-dark-text-secondary font-medium block mb-3">Share As:</label>
              <div className="flex flex-col gap-2.5">
                <label className="flex items-start gap-2.5 cursor-pointer text-sm">
                  <input type="radio" name="shareMode" value="regular" checked={shareMode==="regular"} onChange={e=>setShareMode(e.target.value)} className="mt-0.75 accent-accent cursor-pointer"/>
                  <span className={shareMode==="regular"?"text-light-text dark:text-dark-text":"text-light-text-secondary dark:text-dark-text-secondary"}>
                    <strong>App User</strong>
                    <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">Recipient must sign in to view contract details</div>
                  </span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer text-sm">
                  <input type="radio" name="shareMode" value="reviewer" checked={shareMode==="reviewer"} onChange={e=>setShareMode(e.target.value)} className="mt-0.75 accent-accent cursor-pointer"/>
                  <span className={shareMode==="reviewer"?"text-light-text dark:text-dark-text":"text-light-text-secondary dark:text-dark-text-secondary"}>
                    <strong>Reviewer</strong>
                    <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">Recipient can only view & download documents</div>
                  </span>
                </label>
              </div>
            </div>

            <div className="mb-3.5">
              <label className="text-sm text-light-text-secondary dark:text-dark-text-secondary block mb-1.5 font-medium">Search & Add Reviewers *</label>
              <div className="relative">
                <input
                  value={shareSearchQuery}
                  onChange={e => {setShareSearchQuery(e.target.value); setShowSearchDropdown(true);}}
                  onFocus={() => setShowSearchDropdown(true)}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent"
                />
                {showSearchDropdown && (shareSearchResults.length > 0 || shareSearchQuery.trim()) && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                    {shareSearching && <div className="p-3 text-light-text-secondary dark:text-dark-text-secondary text-sm">Searching...</div>}
                    {!shareSearching && shareSearchResults.length > 0 && (
                      <>
                        {shareSearchResults.map((user, i) => (
                          <button
                            key={i}
                            onClick={() => addReviewer(user)}
                            className="w-full text-left px-3 py-2.5 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary border-b border-light-border dark:border-dark-border last:border-b-0 transition-colors"
                          >
                            <div className="text-light-text dark:text-dark-text text-sm font-medium">{user.displayName}</div>
                            <div className="text-light-text-secondary dark:text-dark-text-secondary text-xs">{user.mail}</div>
                            {user.jobTitle && <div className="text-light-text-tertiary dark:text-dark-text-tertiary text-xs">{user.jobTitle}</div>}
                          </button>
                        ))}
                      </>
                    )}
                    {!shareSearching && shareSearchResults.length === 0 && shareSearchQuery.trim() && (
                      <button
                        onClick={addManualEmail}
                        className="w-full text-left px-3 py-2.5 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-light-text dark:text-dark-text text-sm"
                      >
                        Add "{shareSearchQuery}" as email
                      </button>
                    )}
                  </div>
                )}
              </div>
              {selectedReviewers.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary font-medium">Selected reviewers:</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedReviewers.map((reviewer, i) => (
                      <div key={i} className="inline-flex items-center gap-2 px-3 py-1.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-full text-sm">
                        <span className="text-light-text dark:text-dark-text">{reviewer.displayName || reviewer.mail}</span>
                        <button
                          onClick={() => removeReviewer(reviewer.mail)}
                          className="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text dark:hover:text-dark-text cursor-pointer"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mb-5">
              <label className="text-sm text-light-text-secondary dark:text-dark-text-secondary block mb-1.5 font-medium">Message (optional)</label>
              <textarea value={shareMsg} onChange={e=>setShareMsg(e.target.value)} rows={3} className="w-full px-3 py-2.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-1 focus:ring-accent resize-vertical"/>
            </div>
            <div className="flex gap-2.5 justify-end">
              <button onClick={()=>{setShowShare(false);setShareMode("regular");setSelectedReviewers([]);setShareSearchQuery("");}} className="px-5 py-2.5 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">Cancel</button>
              <button onClick={handleShare} disabled={!selectedReviewers.length && !shareEmail} className="px-6 py-2.5 bg-yellow-600 border-none rounded-lg text-slate-900 cursor-pointer text-sm font-semibold hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Approver Selector Modal */}
      {showExecutionModal && (
        <ApproverSelector
          onSelect={handleInitiateExecution}
          onCancel={() => setShowExecutionModal(false)}
          loading={executionInProgress}
        />
      )}

      {/* CRAF Template Select Modal */}
      {showCrafTemplateModal && (
        <CrafTemplateSelectModal
          service={service}
          onGenerate={handleGenerateCraf}
          onCancel={() => setShowCrafTemplateModal(false)}
          generating={crafGenerating}
        />
      )}

      {/* CRAF Approver Selector Modal — separate approver list from the main contract */}
      {showCrafExecutionModal && (
        <ApproverSelector
          onSelect={handleInitiateCrafExecution}
          onCancel={() => setShowCrafExecutionModal(false)}
          loading={crafExecutionInProgress}
        />
      )}
    </div>
  );
};
